import { Button } from '@ipfabric/ui-components/Button/Button';
import type { RuleFormView } from './useRuleFormViews';

const getAddSelectedLabelsLabel = (count: number) =>
  count > 0 ? `Add ${count} selected labels` : 'Add selected labels';

type RuleFormFooterProps = {
  view: RuleFormView;
  closeModal: () => void;
  formType: 'add' | 'edit';
  isSubmitting: boolean;
  canTest: boolean;
  hasTestModifications: boolean;
  pickerDraftLabelCount: number;
  onApplyPickerDraft: () => void;
  onGoToMainView: () => void;
  onSave: () => void;
  onOpenTestView: () => void;
  onApplyTestChanges: () => void;
  onRevertTestChanges: () => void;
};

const LabelsPickerFooter = ({
  pickerDraftLabelCount,
  onApplyPickerDraft,
  onGoToMainView,
}: Pick<
  RuleFormFooterProps,
  'pickerDraftLabelCount' | 'onApplyPickerDraft' | 'onGoToMainView'
>) => (
  <div className='flex gap-xs'>
    <Button
      className='flex-1'
      dataTestId='rule-form-label-picker-add'
      onClick={onApplyPickerDraft}
      type='button'
      variant='primary'
    >
      {getAddSelectedLabelsLabel(pickerDraftLabelCount)}
    </Button>
    <Button
      className='flex-1'
      dataTestId='rule-form-label-picker-back'
      onClick={onGoToMainView}
      type='button'
      variant='outline'
    >
      Back
    </Button>
  </div>
);

const TestViewFooter = ({
  hasTestModifications,
  onApplyTestChanges,
  onRevertTestChanges,
}: Pick<
  RuleFormFooterProps,
  'hasTestModifications' | 'onApplyTestChanges' | 'onRevertTestChanges'
>) => (
  <div className='flex gap-xs'>
    <Button
      className='flex-1'
      dataTestId='rule-form-test-apply'
      icon='fa-floppy-disk'
      onClick={onApplyTestChanges}
      type='button'
      variant='primary'
    >
      Apply changes
    </Button>
    <Button
      className='flex-1'
      dataTestId='rule-form-test-revert'
      onClick={onRevertTestChanges}
      type='button'
      variant='outline'
    >
      {hasTestModifications ? 'Revert (modified) changes' : 'Revert changes'}
    </Button>
  </div>
);

const MainFormFooter = ({
  formType,
  isSubmitting,
  canTest,
  closeModal,
  onSave,
  onOpenTestView,
}: Pick<
  RuleFormFooterProps,
  | 'formType'
  | 'isSubmitting'
  | 'canTest'
  | 'closeModal'
  | 'onSave'
  | 'onOpenTestView'
>) => (
  <div className='flex gap-xs'>
    <Button
      className='flex-1'
      variant='primary'
      type='button'
      icon='fa-floppy-disk'
      loading={isSubmitting}
      onClick={onSave}
      dataTestId='rule-form-submit'
    >
      {formType === 'add' ? 'Create rule' : 'Update rule'}
    </Button>
    <Button
      className='flex-1'
      variant='outline'
      type='button'
      disabled={!canTest}
      onClick={onOpenTestView}
      dataTestId='rule-form-test'
    >
      Test rule
    </Button>
    <Button className='flex-1' variant='outline' onClick={closeModal}>
      Close
    </Button>
  </div>
);

export const RuleFormFooter = (props: RuleFormFooterProps) => {
  switch (props.view) {
    case 'labels':
      return (
        <LabelsPickerFooter
          pickerDraftLabelCount={props.pickerDraftLabelCount}
          onApplyPickerDraft={props.onApplyPickerDraft}
          onGoToMainView={props.onGoToMainView}
        />
      );
    case 'test':
      return (
        <TestViewFooter
          hasTestModifications={props.hasTestModifications}
          onApplyTestChanges={props.onApplyTestChanges}
          onRevertTestChanges={props.onRevertTestChanges}
        />
      );
    case 'main':
      return (
        <MainFormFooter
          formType={props.formType}
          isSubmitting={props.isSubmitting}
          canTest={props.canTest}
          closeModal={props.closeModal}
          onSave={props.onSave}
          onOpenTestView={props.onOpenTestView}
        />
      );
  }
};
